import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import "./Header.css";

const NAV_LINKS = [
  { label: "Home", to: "/", type: "route" },
  { label: "About", to: "/#about", type: "anchor" },
  { label: "How It Works", to: "/#how-it-works", type: "anchor" },
  { label: "Projects", to: "/#projects", type: "anchor" },
  { label: "FAQ", to: "/#faq", type: "anchor" },
  { label: "Contact", to: "/contact", type: "route" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [hideOnScroll, setHideOnScroll] = useState(false);
  const [lastY, setLastY] = useState(0);
  const navigate = useNavigate();

  /* scroll shrink + hide-on-scroll-down */
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 50);
      setHideOnScroll(y > lastY && y > 120);
      setLastY(y);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [lastY]);

  /* lock body scroll when mobile menu open */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <header
        className={[
          "hdr",
          scrolled ? "hdr--scrolled" : "",
          hideOnScroll ? "hdr--hidden" : "",
          menuOpen ? "hdr--open" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {/* ── Logo ── */}
        <NavLink to="/" className="hdr-logo" onClick={closeMenu}>
          <div className="l-logo-circle">
            <img src="/logo.jpeg" alt="Grow Biz International" />
          </div>
          <div className="hdr-logo-text">
            Grow Biz
            <span>International</span>
          </div>
        </NavLink>
        {/* ── Desktop nav ── */}

        <nav className="hdr-nav">
          {NAV_LINKS.map((l) =>
            l.type === "route" ? (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  "hdr-link" + (isActive ? " hdr-link--active" : "")
                }
              >
                {l.label}
              </NavLink>
            ) : (
              <a
                key={l.to}
                href={l.to}
                className="hdr-link"
                onClick={(e) => {
                  e.preventDefault();
                  const id = l.to.split("#")[1];
                  const el = document.getElementById(id);
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
              >
                {l.label}
              </a>
            ),
          )}
        </nav>
        {/* ── Desktop CTA buttons ── */}
        <div className="hdr-actions">
          <button className="hdr-btn-login" onClick={() => navigate("/login")}>
            Login
          </button>
          <button className="hdr-btn-join" onClick={() => navigate("/join-us")}>
            Join Now
          </button>
        </div>
        {/* ── Hamburger ── */}
        <button
          className={`hdr-burger${menuOpen ? " hdr-burger--open" : ""}`}
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
        >
          <span />
          <span />
          <span />
        </button>
      </header>

      {/* ── Mobile drawer ── */}
      <div
        className={`hdr-drawer${menuOpen ? " hdr-drawer--open" : ""}`}
        aria-hidden={!menuOpen}
      >
        {/* gold top line */}
        <div className="hdr-drawer-line" />

        <nav className="hdr-drawer-nav">
          {NAV_LINKS.map((l, i) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                "hdr-drawer-link" + (isActive ? " hdr-drawer-link--active" : "")
              }
              style={{ animationDelay: `${i * 0.06}s` }}
              onClick={closeMenu}
            >
              <span className="hdr-drawer-num">0{i + 1}</span>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hdr-drawer-actions">
          <button
            className="hdr-btn-login hdr-btn-login--full"
            onClick={() => {
              closeMenu();
              navigate("/login");
            }}
          >
            Login
          </button>
          <button
            className="hdr-btn-join hdr-btn-join--full"
            onClick={() => {
              closeMenu();
              navigate("/join-us");
            }}
          >
            Join Now
          </button>
        </div>

        <div className="hdr-drawer-footer">
          <p>© 2026 Grow Biz International</p>
        </div>
      </div>

      {/* ── Overlay ── */}
      {menuOpen && (
        <div className="hdr-overlay" onClick={closeMenu} aria-hidden="true" />
      )}
    </>
  );
}
